import { Disclosure } from "@heroui/react";

import { useLocale, useTranslations } from "@/src/i18n/i18n";
import { formatScore } from "@/src/pages/Posts/moderationScores";
import type { ModerationScores, PostPhoto } from "@/src/pages/Posts/posts.type";

type ScoreDetailsProps = {
  scores: ModerationScores | null;
  /**
   * Les photos de la publication, **dans l'ordre du serveur** — le même que
   * celui de `perPhoto`, qui est écrit en les parcourant.
   *
   * Sert à donner à chaque ligne la clé de la photo qu'elle décrit plutôt que
   * son rang. Les deux tableaux peuvent diverger en longueur (des scores
   * enregistrés avant qu'une photo soit effacée), d'où le repli sur le rang.
   */
  photos: PostPhoto[];
};

/**
 * Le détail par photo et par catégorie, replié par défaut.
 *
 * C'est ce tableau qui permet de **calibrer le seuil** sur des cas réels : la
 * pastille dit le verdict, celui-ci dit pourquoi. Replié, parce qu'on parcourt
 * une file en regardant les images, pas les nombres — on ne les ouvre que
 * lorsqu'un cas surprend.
 *
 * ⚠️ **`Disclosure` et non un bouton maison.** Écrit à la main, le contrôle
 * n'était qu'un texte gris de la taille d'une légende : sur un écran dont la
 * raison d'être est de montrer les scores, l'affordance qui les ouvre ne peut
 * pas être le seul texte inerte de la carte. La primitive apporte le chevron et
 * l'état `aria-expanded`, et c'est déjà celle de l'aide des signalements.
 *
 * ⚠️ **Les catégories affichées sont celles que le serveur a réellement
 * évaluées**, pas une liste écrite ici. Le modèle n'évalue que six catégories
 * sur une image ; les autres — dont `sexual/minors` — sont texte uniquement et
 * renvoient 0. Les afficher laisserait croire qu'elles ont été vérifiées.
 */
export function ScoreDetails({ scores, photos }: ScoreDetailsProps) {
  const t = useTranslations("posts");
  const { locale } = useLocale();

  if (!scores?.perPhoto?.length) return null;

  const lignes = scores.perPhoto.map((categories, rang) => ({
    key: photos[rang]?.id ?? `rang-${rang}`,
    position: rang + 1,
    categories,
  }));

  return (
    <Disclosure className="text-xs">
      <Disclosure.Trigger className="flex w-fit items-center gap-1.5 py-1">
        {t("scores-show")}
        <Disclosure.Indicator />
      </Disclosure.Trigger>

      <Disclosure.Content>
        <Disclosure.Body className="flex flex-col gap-2 px-0 pb-2">
          {scores.model ? (
            <p className="text-muted">
              {t("scores-model", {
                model: scores.model,
                threshold: formatScore(scores.threshold ?? 0.5, locale),
              })}
            </p>
          ) : null}

          {lignes.map((ligne) => (
            <div
              className="rounded-md border border-separator p-2"
              key={ligne.key}
            >
              <p className="mb-1 font-medium">
                {t("scores-photo", { position: ligne.position })}
              </p>

              {ligne.categories === null ? (
                // Distinct d'une photo propre : celle-ci n'a pas pu être jugée.
                <p className="text-warning">{t("score-unjudged")}</p>
              ) : (
                <ul className="flex flex-col gap-0.5">
                  {/* La plus forte d'abord : c'est elle qui a décidé du sort de
                      la publication, les autres ne sont que du contexte. */}
                  {Object.entries(ligne.categories)
                    .toSorted(([, a], [, b]) => b - a)
                    .map(([category, score]) => (
                      <li className="flex justify-between gap-4" key={category}>
                        <span className="text-muted">{category}</span>
                        <span className="tabular-nums">
                          {formatScore(score, locale)}
                        </span>
                      </li>
                    ))}
                </ul>
              )}
            </div>
          ))}
        </Disclosure.Body>
      </Disclosure.Content>
    </Disclosure>
  );
}
